import { doc, getDoc } from 'firebase/firestore';
import { configure, reaction } from 'mobx';
import { RouterStore, startRouter } from 'mobx-router';

import { getFirestore } from 'services/Firebase';
import { DocumentType } from 'types/DocumentType';
import {
  type ClientCoordinate,
  type CreativeCoordinate,
  inferDocumentType,
  type ProjectCoordinate,
  type TearsheetCoordinate,
} from 'types/RouteTypes';
import { UserEvents } from 'types/UserEvents';
import { ViewModes } from 'types/ViewModes';
import { fillRoute } from 'utils/RouteUtils';

import AdminManager from './AdminManager';
import Animation from './Animation';
import AnimationPreview from './AnimationPreview';
import { getAppBroker } from './AppBroker';
import Client from './Client';
import ClientsManager from './ClientsManager';
import Creative from './Creative';
import * as DatabasePaths from './DatabasePaths';
import DocumentStore from './DocumentStore';
import Project from './Project';
import routes from './Routes';
import Tearsheet from './Tearsheet';
import UI from './UI';
import User from './User';
import UsersCache from './UsersCache';

// The `AppView` type captures the types that `RootStore.current` is allowed to
// hold. It's derived from actual usage throughout the codebase, rather than
// designed; built by starting from `never` and adding whichever type the
// compiler demanded next, so every member is here because some code path
// actually assigns it.
//
// The absences carry as much information as the members. AdminManager and the
// dashboard (labelled Home in the UI) are missing because those views never
// become current: Admin.tsx reads store.admin, Dashboard.tsx reads
// store.user.starred, and both routes call setCurrent(undefined) on entry to
// clear whatever document the previous view left behind. ClientsManager, by
// contrast, is present because loadClients assigns it. So adding a member is a
// decision about what may be the current document - not a way to silence a
// type error.
export type AppView =
  | Animation
  | AnimationPreview
  | Client
  | ClientsManager
  | Creative
  | Project
  | Tearsheet;

configure({
  enforceActions: 'never',
});

// The slice of DocumentStore that liveAt needs.
interface LoadableDocument {
  readonly path: string;
  readonly loading: boolean;
  readonly loaded: boolean;
}

// Returns `document` if it is for `path` and still live - loading or loaded -
// so a load can reuse it. Being assigned is not enough: views reset their
// store on unmount (Creative, Animation, AnimationPreview), which leaves the
// RootStore field pointing at a store with no data and no snapshot listener.
//
// Deliberately not a `document is T` predicate: TypeScript narrows the false
// branch to exclude T, but false here can also mean "a T that isn't live".
function liveAt<T extends LoadableDocument>(
  document: T | undefined,
  path: string
): T | undefined {
  if (
    document !== undefined &&
    document.path === path &&
    (document.loading || document.loaded)
  ) {
    return document;
  }
  return undefined;
}

// The load() promise of each document a load method started, so reusing a
// document whose load is still in flight can wait for its data rather than
// return it empty. Module-level and weak rather than RootStore fields:
// becomeObservable would make fields observable, and an entry should go
// when its document does.
const documentLoads = new WeakMap<DocumentStore, Promise<unknown>>();

function startLoad(document: DocumentStore): Promise<unknown> {
  const loading = document.load();
  documentLoads.set(document, loading);
  return loading;
}

function joinLoad(document: DocumentStore): Promise<unknown> | undefined {
  // does very little; mostly for symmetry with startLoad.
  return documentLoads.get(document);
}

export interface LoadCreativeOptions {
  filterSetName?: string;
  adID?: string;
}

// test project path: '/clients/CImTDuGgZixPziFqlWuZ/projects/07nQAaDwHhZDdwcPkV7T'

export class RootStore extends DocumentStore<any, any> {
  current: AppView | undefined = undefined;
  client: Client | undefined = undefined;
  project: Project | undefined = undefined;
  creative: Creative | undefined = undefined;
  tearsheet: Tearsheet | undefined = undefined;
  animation: Animation | undefined = undefined;
  preview: AnimationPreview | undefined = undefined;
  documentType: DocumentType = DocumentType.NONE;

  clients = new ClientsManager(this, undefined);
  admin = new AdminManager(this);
  // dashboard = new DashboardManager(this); // TODO: create dashboard manager

  ui = new UI();
  user = new User(this);
  router: RouterStore<any> = new RouterStore(this);
  routes = routes;

  usersCache = new UsersCache();

  constructor() {
    super();
    console.log('ROOT: constructor');
    this.becomeObservable({ except: ['routes'] });

    this.user.addEventListener(UserEvents.LOGIN_SUCCESS, () => {
      console.log('ROOT: login success');
      this.router.goTo(routes.clients);
    });

    this.user.addEventListener(UserEvents.LOGGED_OUT, () => {
      console.log('ROOT: logged out');
      this.router.goTo(routes.notAuthenticated);
      this.reset();
    });

    this.connectAuth();

    reaction(
      () => this.router.queryParams,
      (queryParams, prevQueryParams) => {
        const typedQueryParams = queryParams as { mode?: ViewModes };

        if (!typedQueryParams?.mode) {
          this.ui.viewMode = ViewModes.DESKTOP;
          return;
        }

        if (queryParams?.mode !== prevQueryParams?.mode) {
          this.ui.viewMode = typedQueryParams.mode;
        }
      }
    );
  }

  connectAuth = async () => {
    await this.user.connect();
    console.log('ROOT: connected');
    startRouter(this.routes, this, { html5history: true });
  };

  loadAnimationPreview = async (
    animationCoordinate: CreativeCoordinate,
    assetFilename: string
  ) => {
    this.setDocumentType(DocumentType.ANIMATION);
    const db = getFirestore();
    const path = DatabasePaths.creative.fill(animationCoordinate);
    const publicDocRef = doc(db, path, 'public', 'info');
    const privateDocRef = doc(db, path);

    // The document identifies the creative, not the file, so a reused preview
    // still needs the file applied. AnimationPreviewStageFrame reloads its
    // player when previewURL changes.
    const livePreview = liveAt(this.preview, publicDocRef.path);
    if (livePreview) {
      livePreview.setPreviewURL(assetFilename);
      this.setCurrent(livePreview);
      await joinLoad(livePreview);
      return livePreview;
    }

    const incomingAnimationPreview = new AnimationPreview(
      this,
      publicDocRef,
      privateDocRef
    );
    this.preview = incomingAnimationPreview;
    incomingAnimationPreview.setPreviewURL(assetFilename);
    this.setCurrent(incomingAnimationPreview);
    await startLoad(incomingAnimationPreview);
    return incomingAnimationPreview;
  };

  loadAnimation = async (animationCoordinate: CreativeCoordinate) => {
    this.setDocumentType(DocumentType.ANIMATION);
    const docRef = doc(
      getFirestore(),
      DatabasePaths.creative.fill(animationCoordinate)
    );
    const liveAnimation = liveAt(this.animation, docRef.path);
    if (liveAnimation) {
      this.setCurrent(liveAnimation);
      await joinLoad(liveAnimation);
      return liveAnimation;
    }

    this.animation?.reset();
    const incomingAnimation = new Animation(this, docRef);
    this.animation = incomingAnimation;
    this.setCurrent(incomingAnimation);
    await startLoad(incomingAnimation);
    return incomingAnimation;
  };

  // Returns the creative rather than leaving callers to re-read
  // `store.creative` afterwards. Both are the same object, but the return
  // value carries the guarantee in the type: `new Creative(...)` below is
  // unconditional and runs before the first await, so once this resolves
  // there is always a creative. Only a throw can prevent that, and a throw
  // skips the caller's continuation anyway.
  //
  // The options are applied after the data arrives, on both the reuse and
  // the load path, so callers never have to reapply them. With no adID the
  // current ad is cleared, which is what deselects it on ad -> creative
  // navigation.
  //
  // This makes concurrent calls safe: a call that arrives while another's
  // load is in flight awaits the same promise, and the two resume in the
  // order they were made - so the latest call's selection wins.
  loadCreative = async (
    creativeCoordinate: CreativeCoordinate,
    { filterSetName, adID }: LoadCreativeOptions = {}
  ): Promise<Creative> => {
    this.setDocumentType(DocumentType.CREATIVE);
    const docRef = doc(
      getFirestore(),
      DatabasePaths.creative.fill(creativeCoordinate)
    );

    // Held locally as well as assigned, so everything after the await acts
    // on the creative this call resolved rather than on whatever
    // `this.creative` has become in the meantime.
    let creative = liveAt(this.creative, docRef.path);
    if (creative) {
      this.setCurrent(creative);
      await joinLoad(creative);
    } else {
      this.creative?.reset();
      creative = new Creative(this, docRef);
      this.creative = creative;
      this.setCurrent(creative);
      await startLoad(creative);
    }

    if (filterSetName) {
      creative.ads.filter.setCurrentByName(filterSetName);
    }
    if (adID) {
      creative.ads.setCurrent(adID);
    } else {
      creative.ads.clearCurrent();
    }

    return creative;
  };

  loadCreativeOrAnimation = async (
    coordinate: CreativeCoordinate,
    { filterSetName, adID }: LoadCreativeOptions = {}
  ): Promise<Creative | Animation | null> => {
    const ref = doc(getFirestore(), DatabasePaths.creative.fill(coordinate));

    // Already loaded (or loading) means the type is known, so skip the
    // round trip. That also keeps loadCreative's latest-call-wins ordering:
    // with nothing awaited before it, concurrent calls reach it in the order
    // they were made, rather than in the order Firestore answers.
    if (liveAt(this.creative, ref.path)) {
      return this.loadCreative(coordinate, { filterSetName, adID });
    }
    if (liveAt(this.animation, ref.path)) {
      return this.loadAnimation(coordinate);
    }

    const snap = await getDoc(ref);
    const type = snap.get('type');
    switch (type) {
      case DocumentType.CREATIVE:
        return this.loadCreative(coordinate, { filterSetName, adID });
      case DocumentType.ANIMATION:
        return this.loadAnimation(coordinate);
      default:
        console.error(`Unexpected document type ${type} at path ${ref.path}`);
        return null;
    }
  };

  loadProject = async (projectCoordinate: ProjectCoordinate) => {
    this.setDocumentType(DocumentType.PROJECT);

    const docRef = doc(
      getFirestore(),
      DatabasePaths.project.fill(projectCoordinate)
    );
    const liveProject = liveAt(this.project, docRef.path);
    if (liveProject) {
      this.setCurrent(liveProject);
      await joinLoad(liveProject);
      return liveProject;
    }

    this.project?.reset();
    const incomingProject = new Project(this, docRef);
    this.project = incomingProject;
    this.setCurrent(incomingProject);
    await startLoad(incomingProject);
    return incomingProject;
  };

  loadClient = async (clientCoordinate: ClientCoordinate) => {
    this.setDocumentType(DocumentType.CLIENT);

    const docRef = doc(
      getFirestore(),
      DatabasePaths.client.fill(clientCoordinate)
    );
    const liveClient = liveAt(this.client, docRef.path);
    if (liveClient) {
      this.setCurrent(liveClient);
      await joinLoad(liveClient);
      return liveClient;
    }

    this.client?.reset();
    const incomingClient = new Client(this, docRef);
    this.client = incomingClient;
    this.setCurrent(incomingClient);
    await startLoad(incomingClient);
    return incomingClient;
  };

  loadTearsheet = async (tearsheetCoordinate: TearsheetCoordinate) => {
    this.setDocumentType(DocumentType.TEARSHEET);

    const docRef = doc(
      getFirestore(),
      DatabasePaths.tearsheet.fill(tearsheetCoordinate)
    );
    const liveTearsheet = liveAt(this.tearsheet, docRef.path);
    if (liveTearsheet) {
      this.setCurrent(liveTearsheet);
      await joinLoad(liveTearsheet);
      return liveTearsheet;
    }

    this.tearsheet?.reset();
    const incomingTearsheet = new Tearsheet(this, docRef);
    this.tearsheet = incomingTearsheet;
    this.setCurrent(incomingTearsheet);
    await startLoad(incomingTearsheet);
    return incomingTearsheet;
  };

  // Both the document type and the current view are set whether or not a load
  // is needed. The clients route used to skip loadClients entirely once the
  // collection was mounted, doing only setCurrent - so returning to /clients
  // after viewing a creative left documentType as CREATIVE, which
  // BreadcrumbManager and getDocumentTypeLabel both read.
  //
  // The mounted check lives here rather than at the call site because
  // DocumentStore.load() has no such guard of its own: it re-fetches and
  // re-registers its snapshot listener every time, so something has to hold
  // this knowledge, and the callee is the honest place for it.
  loadClients = async () => {
    this.setDocumentType(DocumentType.NONE);
    this.setCurrent(this.clients);
    if (!this.clients.mounted) {
      await this.clients.load();
    }
  };

  setCurrent = (view: AppView | undefined) => {
    this.current = view;
  };

  setDocumentType = (type: DocumentType) => {
    this.documentType = type;
  };

  reset = () => {
    this.current?.reset();
    this.user.reset();
    this.clients.reset();
    this.admin?.reset();
    this.project?.reset();
    this.creative?.reset();
  };
}

const store = new RootStore();

function routeNameForDocumentType(documentType: DocumentType): string {
  // for the most part, the route name is the same as the document type, with the following exceptions
  switch (documentType) {
    case DocumentType.CREATIVE:
    case DocumentType.ANIMATION:
      return 'creative';
    case DocumentType.TEARSHEET:
      return 'tearsheetRoot';
    case DocumentType.TEARSHEET_AD:
      return 'tearsheet';
    default:
      return documentType;
  }
}

getAppBroker()
  .registerHandler('getCurrentUser', () => store.user)
  .registerHandler('navigate', async (pathParams, options?) => {
    const docType = options?.documentTypeHint || inferDocumentType(pathParams);
    if (!docType) {
      console.error(
        `Could not infer document type for ${JSON.stringify(pathParams)}`
      );
      return;
    }

    const route = routes[routeNameForDocumentType(docType)];
    if (route) {
      console.log('Navigating to route:', route);

      const queryParams = options?.keepQueryString
        ? store.router.queryParams
        : undefined;

      store.setDocumentType(docType);
      await store.router.goTo(route, pathParams, queryParams);
      return;
    }
    // Handle the error case, perhaps navigate to a default route
    console.error(`No route found for document type: ${docType}`);
  })

  .registerHandler('routeForDocument', (pathParams, options?) => {
    const docType = options?.documentTypeHint || inferDocumentType(pathParams);
    if (docType) {
      const route = routes[routeNameForDocumentType(docType)];
      if (route) {
        return fillRoute(route, pathParams);
      }
    }
    return '';
  })

  .registerHandler('navigateAfterLogin', () => {
    // formerly located in stores/User.ts
    return store.router.currentRoute === store.routes.notAuthenticated
      ? store.router.goTo(store.routes.clients)
      : Promise.resolve();
  })

  .registerHandler('afterLogout', () => {
    // formerly located in stores/User.ts
    store.current?.reset();
    store.setCurrent(undefined);
  });

export default store;
